import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Location, Link, useSearchParams } from 'react-router';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { LoginRequest } from '../types/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faExclamationTriangle } from '@/icons';
import { CustomToggle } from '../components/CustomToggle';
import { debugError } from '../utils/debug';
import { Modal } from '../components/Modal';
import { SpinnerOverlay } from '../components/SpinnerOverlay';
import { authApiService } from '../services/authApiService';
import { getMapHealthChecksUrl } from '../config/environment';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

/**
 * Standard login page component
 * Handles user authentication with email and password
 */
export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAuthenticated, loading, error, login, clearError, user } = useAuthContext();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const selfServeSignup = useFeatureFlag('SelfServiceSignup');
    const socialSignInEnabled = useFeatureFlag('SocialSignIn');

    const [formData, setFormData] = useState<LoginRequest>({
        username: '',
        password: '',
        rememberMe: true,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordInputType, setPasswordInputType] = useState<'password' | 'text'>('password');
    const [hasRedirected, setHasRedirected] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        username?: string;
        password?: string;
    }>({});
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [isSendingReset, setIsSendingReset] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);
    const [oauthError, setOauthError] = useState<string | null>(null);
    const [oauthBusy, setOauthBusy] = useState<'google' | 'apple' | 'microsoft' | null>(null);
    const [siteOffline, setSiteOffline] = useState(false);

    // Redirect if already authenticated (but not if we just logged in - that's handled in handleSubmit)
    useEffect(() => {
        if (isAuthenticated && !isSubmitting && !hasRedirected) {
            // Get the redirect path from location state (from ProtectedRoute redirect)
            // or from sessionStorage (fallback for direct URL navigation). Include search (query string).
            const from = (location.state as { from?: Location })?.from;
            const storedPath = sessionStorage.getItem('redirectAfterLogin');
            const redirectPath = (from ? from.pathname + (from.search || '') : null) || storedPath || '/';

            // Debug logging
            console.log('[LoginPage] Redirect (already authenticated):', {
                fromState: from?.pathname,
                fromSearch: from?.search,
                storedPath,
                finalPath: redirectPath
            });

            // Clear the stored redirect path after using it
            if (storedPath) {
                sessionStorage.removeItem('redirectAfterLogin');
            }

            setHasRedirected(true);
            navigate(redirectPath, { replace: true });
        }
    }, [isAuthenticated, navigate, location, isSubmitting, hasRedirected]);

    // Clear errors when component mounts or form changes
    useEffect(() => {
        if (error) {
            clearError();
        }
    }, [formData, clearError]);

    // Set page title
    useEffect(() => {
        document.title = 'Login | Calculist';
    }, []);

    useEffect(() => {
        const oauthErr = searchParams.get('oauthError');
        const prefillEmail = searchParams.get('email');
        if (oauthErr) {
            setOauthError(decodeURIComponent(oauthErr));
            setSearchParams({}, { replace: true });
        } else if (prefillEmail) {
            setFormData(prev => ({ ...prev, username: decodeURIComponent(prefillEmail) }));
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const resolveLoginReturnPath = (): string => {
        const from = (location.state as { from?: Location })?.from;
        const storedPath = sessionStorage.getItem('redirectAfterLogin');
        return (from ? from.pathname + (from.search || '') : null) || storedPath || '/cases/list';
    };

    const handleGoogleLogin = async () => {
        clearError();
        setOauthError(null);
        setOauthBusy('google');
        try {
            const returnPath = resolveLoginReturnPath();
            const { data } = await authApiService.authOAuthGoogleLoginStart(returnPath);
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setOauthError('Google sign-in is not available.');
        } catch (err: unknown) {
            debugError('authOAuthGoogleLoginStart', err);
            setOauthError(err instanceof Error ? err.message : 'Could not start Google sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    const handleAppleLogin = async () => {
        clearError();
        setOauthError(null);
        setOauthBusy('apple');
        try {
            const returnPath = resolveLoginReturnPath();
            const { data } = await authApiService.authOAuthAppleLoginStart(returnPath);
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setOauthError('Apple sign-in is not available.');
        } catch (err: unknown) {
            debugError('authOAuthAppleLoginStart', err);
            setOauthError(err instanceof Error ? err.message : 'Could not start Apple sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    const handleMicrosoftLogin = async () => {
        clearError();
        setOauthError(null);
        setOauthBusy('microsoft');
        try {
            const returnPath = resolveLoginReturnPath();
            const { data } = await authApiService.authOAuthMicrosoftLoginStart(returnPath);
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setOauthError('Microsoft sign-in is not available.');
        } catch (err: unknown) {
            debugError('authOAuthMicrosoftLoginStart', err);
            setOauthError(err instanceof Error ? err.message : 'Could not start Microsoft sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    // Warm up the API when login page is displayed (fire-and-forget)
    useEffect(() => {
        const healthUrl = getMapHealthChecksUrl();
        fetch(healthUrl).catch(() => {});
    }, []);

    // Check whether the site is offline to normal users, so we can warn before submission.
    // Login itself still works for Admin/Root — this is informational only.
    useEffect(() => {
        authApiService.getSiteStatus()
            .then(({ siteOffline }) => setSiteOffline(siteOffline))
            .catch(() => {});
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        // Clear validation error for this field
        if (validationErrors[name as keyof typeof validationErrors]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const togglePasswordVisibility = () => {
        const newShowPassword = !showPassword;
        setShowPassword(newShowPassword);
        // Update the input type for password managers - they need it to stay as 'password' initially
        setPasswordInputType(newShowPassword ? 'text' : 'password');
    };

    const validateForm = (): boolean => {
        const errors: typeof validationErrors = {};

        if (!formData.username.trim()) {
            errors.username = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
            errors.username = 'Please enter a valid email address';
        }

        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setValidationErrors({});
        setOauthError(null);

        try {
            await login(formData);

            // Clear password field immediately after successful login for security
            // This also helps password managers detect successful login
            setFormData(prev => ({ ...prev, password: '' }));

            // Check if user has accepted T&C - if not, redirect to T&C acceptance page
            // Wait for auth context to update with user data
            await new Promise(resolve => setTimeout(resolve, 150));

            // Check user status from context (user is set by login function)
            // The user state should be updated by now
            if (user?.status === 'Pending Acceptance of Terms and Conditions') {
                // Store the intended redirect path for after T&C acceptance (include query string)
                const from = (location.state as { from?: Location })?.from;
                const storedPath = sessionStorage.getItem('redirectAfterLogin');
                const redirectPath = (from ? from.pathname + (from.search || '') : null) || storedPath || '/';
                if (redirectPath && redirectPath !== '/auth/accept-terms') {
                    sessionStorage.setItem('redirectAfterAcceptTerms', redirectPath);
                }
                // Clear the stored redirect path since we're redirecting to T&C
                if (storedPath) {
                    sessionStorage.removeItem('redirectAfterLogin');
                }
                navigate('/auth/accept-terms', { replace: true });
                return;
            }

            // After successful login, redirect to the original path if available
            // Get the redirect path from location state (from ProtectedRoute redirect)
            // or from sessionStorage (fallback for direct URL navigation). Include search (query string).
            const from = (location.state as { from?: Location })?.from;
            const storedPath = sessionStorage.getItem('redirectAfterLogin');
            const redirectPath = (from ? from.pathname + (from.search || '') : null) || storedPath || '/';

            // Debug logging
            console.log('[LoginPage] Redirect after login:', {
                fromState: from?.pathname,
                fromSearch: from?.search,
                storedPath,
                finalPath: redirectPath,
                locationState: location.state
            });

            // Mark that we've redirected BEFORE clearing sessionStorage to prevent race conditions
            setHasRedirected(true);

            // Clear the stored redirect path after using it
            if (storedPath) {
                sessionStorage.removeItem('redirectAfterLogin');
            }

            // Small delay to allow password managers to detect successful login
            // Password managers look for: form submission -> navigation -> form disappears
            await new Promise(resolve => setTimeout(resolve, 100));

            // Navigate immediately after login
            // Use window.location.href for wizard routes to ensure full page load and avoid React Router conflicts
            if (redirectPath.startsWith('/wizard/')) {
                window.location.href = redirectPath;
                return; // Exit early since we're doing a full page reload
            } else {
                navigate(redirectPath, { replace: true });
            }
        } catch (error) {
            // Error is handled by the auth context
            debugError('Login failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isSubmitting) {
            e.preventDefault();
            handleSubmit(e as React.FormEvent);
        }
    };

    const handleSendResetLink = async () => {
        if (!forgotPasswordEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
            return;
        }
        setIsSendingReset(true);
        setResetError(null);
        try {
            await authApiService.forgotPassword(forgotPasswordEmail);
            setResetEmailSent(true);
        } catch (error: any) {
            debugError('Failed to send reset email:', error);
            // Check if it's a technical error (500) vs business rule (200)
            // BaseApiService sets error.status on the error object
            if (error?.status === 500) {
                // Technical error - show error message
                setResetError('There was a problem sending the reset password email. Please try again later.');
                setResetEmailSent(false);
            } else {
                // Business rule or other - show success (don't leak information)
                setResetEmailSent(true);
            }
        } finally {
            setIsSendingReset(false);
        }
    };

    return (
        <>
            <SpinnerOverlay isVisible={loading && !isSubmitting} message="Initializing..." />
            {siteOffline && (
                <div style={{
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    textAlign: 'center',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 700,
                }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
                    Site Offline Temporarily - Try Again In A Few Minutes
                </div>
            )}
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px', // 2x the default 32px
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                        <p style={themedStyles.text.muted}>Sign in to your account</p>
                    </div>

                    {(error || oauthError) && (
                        <div style={themedStyles.alert.error}>
                            <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                            <div>
                                <strong>Login Failed</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                    {error || oauthError}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    clearError();
                                    setOauthError(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '20px',
                                    color: theme.colors.status.error,
                                    cursor: 'pointer',
                                    padding: '0',
                                    marginLeft: 'auto',
                                    flexShrink: 0,
                                }}
                                aria-label="Dismiss error"
                            >
                                ×
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }} autoComplete="on" role="form" aria-label="Login form" method="post" action="/api/v1/auth/login" name="loginForm">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                            <label htmlFor="username" style={themedStyles.text.label}>
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your email"
                                disabled={isSubmitting}
                                style={{
                                    ...themedStyles.input.default,
                                    ...(validationErrors.username ? themedStyles.input.error : {}),
                                }}
                                autoComplete="username email"
                                autoFocus
                                data-testid="email-input"
                                aria-describedby={validationErrors.username ? "username-error" : undefined}
                                aria-invalid={!!validationErrors.username}
                            />
                            {validationErrors.username && (
                                <span id="username-error" style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>
                                    {validationErrors.username}
                                </span>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                            <label htmlFor="password" style={themedStyles.text.label}>
                                Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={passwordInputType}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter your password"
                                    disabled={isSubmitting}
                                    style={{
                                        ...themedStyles.input.default,
                                        ...(validationErrors.password ? themedStyles.input.error : {}),
                                        paddingRight: '40px', // Make room for the eye icon
                                    }}
                                    autoComplete="password"
                                    data-testid="password-input"
                                    aria-describedby={validationErrors.password ? "password-error" : undefined}
                                    aria-invalid={!!validationErrors.password}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    disabled={isSubmitting}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.colors.text.secondary,
                                        fontSize: '16px',
                                        opacity: isSubmitting ? 0.5 : 1,
                                    }}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    tabIndex={0}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            {validationErrors.password && (
                                <span id="password-error" style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>
                                    {validationErrors.password}
                                </span>
                            )}
                        </div>

                        <div style={{
                            padding: `${theme.spacing.xs} 0`,
                        }}>
                            <CustomToggle
                                checked={formData.rememberMe ?? false}
                                onChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: checked }))}
                                label="Remember Me"
                                disabled={isSubmitting}
                                style={{
                                    marginBottom: theme.spacing.xs,
                                }}
                            />
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.text.muted,
                                lineHeight: 1.4,
                                gap: theme.spacing.md,
                            }}>
                                <span>Keep me signed in on this device</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setForgotPasswordEmail(formData.username);
                                        setShowForgotPasswordModal(true);
                                        setResetEmailSent(false);
                                        setResetError(null);
                                    }}
                                    disabled={isSubmitting}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        fontSize: theme.typography.fontSize.sm,
                                        textDecoration: 'none',
                                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                        margin: 0,
                                        opacity: isSubmitting ? 0.5 : 1,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Forgot Password?
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                ...themedStyles.button.primary,
                                ...(isSubmitting ? themedStyles.button.disabled : {}),
                                padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                                fontSize: theme.typography.fontSize.lg,
                                fontWeight: theme.typography.fontWeight.semibold,
                                marginTop: 0,
                            }}
                            data-testid="login-button"
                        >
                            {isSubmitting ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid transparent',
                                        borderTop: '2px solid currentColor',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                    }}></div>
                                    Signing In...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    {socialSignInEnabled && (
                    <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.md,
                        marginTop: theme.spacing.sm,
                        marginBottom: theme.spacing.sm,
                    }}>
                        <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                        <span style={{ ...themedStyles.text.muted, fontSize: theme.typography.fontSize.sm, whiteSpace: 'nowrap' }}>Or use another method</span>
                        <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <button
                            type="button"
                            onClick={() => void handleGoogleLogin()}
                            disabled={!!oauthBusy || isSubmitting}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.sm,
                                width: '100%',
                                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.medium,
                                borderRadius: 8,
                                border: '1px solid #dadce0',
                                backgroundColor: '#ffffff',
                                color: '#3c4043',
                                cursor: oauthBusy || isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: oauthBusy || isSubmitting ? 0.7 : 1,
                            }}
                        >
                            <i className="fab fa-google" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                            {oauthBusy === 'google' ? 'Redirecting…' : 'Sign in with Google'}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleMicrosoftLogin()}
                            disabled={!!oauthBusy || isSubmitting}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.sm,
                                width: '100%',
                                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.medium,
                                borderRadius: 8,
                                border: 'none',
                                backgroundColor: '#0078D4',
                                color: '#ffffff',
                                cursor: oauthBusy || isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: oauthBusy || isSubmitting ? 0.7 : 1,
                            }}
                        >
                            <i className="fab fa-microsoft" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                            {oauthBusy === 'microsoft' ? 'Redirecting…' : 'Sign in with Microsoft'}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleAppleLogin()}
                            disabled={!!oauthBusy || isSubmitting}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.sm,
                                width: '100%',
                                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.medium,
                                borderRadius: 8,
                                border: 'none',
                                backgroundColor: '#000000',
                                color: '#ffffff',
                                cursor: oauthBusy || isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: oauthBusy || isSubmitting ? 0.7 : 1,
                            }}
                        >
                            <i className="fab fa-apple" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                            {oauthBusy === 'apple' ? '…' : 'Sign in with Apple'}
                        </button>
                    </div>
                    </>
                    )}

                    {selfServeSignup && (
                        <div style={{ marginTop: theme.spacing.lg, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm }}>
                            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>First time to Calculist?</span>
                            <Link
                                to="/auth/register/plan"
                                onClick={isSubmitting ? (e) => e.preventDefault() : undefined}
                                style={{
                                    display: 'inline-block',
                                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                                    borderRadius: theme.borderRadius.md,
                                    backgroundColor: theme.colors.primary,
                                    color: '#fff',
                                    textDecoration: 'none',
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    opacity: isSubmitting ? 0.5 : 1,
                                    pointerEvents: isSubmitting ? 'none' : undefined,
                                }}
                            >
                                Sign Up Your Team
                            </Link>
                        </div>
                    )}

                    <Modal
                        isOpen={showForgotPasswordModal}
                        onClose={() => {
                            setShowForgotPasswordModal(false);
                            setResetEmailSent(false);
                            setForgotPasswordEmail('');
                        }}
                        title="Reset Password"
                    >
                        {!resetEmailSent ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                                    <p style={{
                                        ...themedStyles.text.body,
                                        margin: 0,
                                        color: theme.colors.text.primary,
                                        lineHeight: 1.6,
                                    }}>
                                        Enter your email address below and we'll send you a password reset link.
                                    </p>
                                    <p style={{
                                        ...themedStyles.text.body,
                                        margin: 0,
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.secondary,
                                        lineHeight: 1.5,
                                    }}>
                                        If an account exists with that email, you'll receive instructions to reset your password.
                                    </p>
                                </div>
                                {resetError && (
                                    <div style={themedStyles.alert.error}>
                                        <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                                        <div style={{ flex: 1 }}>
                                            <strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                                                Error
                                            </strong>
                                            <p style={{
                                                margin: 0,
                                                fontSize: theme.typography.fontSize.sm,
                                                color: theme.colors.status.error,
                                                lineHeight: 1.5,
                                            }}>
                                                {resetError}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setResetError(null)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '20px',
                                                color: theme.colors.status.error,
                                                cursor: 'pointer',
                                                padding: '0',
                                                marginLeft: 'auto',
                                                flexShrink: 0,
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: theme.borderRadius.sm,
                                                transition: 'background-color 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                            aria-label="Dismiss error"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                                    <label htmlFor="forgot-email" style={themedStyles.text.label}>
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="forgot-email"
                                        value={forgotPasswordEmail}
                                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !isSendingReset && forgotPasswordEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
                                                e.preventDefault();
                                                handleSendResetLink();
                                            }
                                        }}
                                        placeholder="Enter your email"
                                        disabled={isSendingReset}
                                        style={{
                                            ...themedStyles.input.default,
                                            opacity: isSendingReset ? 0.6 : 1,
                                        }}
                                        autoFocus
                                    />
                                </div>
                                <div style={{
                                    display: 'flex',
                                    gap: theme.spacing.md,
                                    justifyContent: 'flex-end',
                                    marginTop: theme.spacing.sm,
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowForgotPasswordModal(false);
                                            setResetEmailSent(false);
                                            setForgotPasswordEmail('');
                                            setResetError(null);
                                        }}
                                        disabled={isSendingReset}
                                        style={{
                                            ...themedStyles.button.secondary,
                                            opacity: isSendingReset ? 0.5 : 1,
                                            cursor: isSendingReset ? 'not-allowed' : 'pointer',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSendingReset) {
                                                e.currentTarget.style.opacity = '0.9';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSendingReset) {
                                                e.currentTarget.style.opacity = '1';
                                            }
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSendResetLink}
                                        disabled={isSendingReset || !forgotPasswordEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)}
                                        style={{
                                            ...themedStyles.button.primary,
                                            opacity: (isSendingReset || !forgotPasswordEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) ? 0.5 : 1,
                                            cursor: (isSendingReset || !forgotPasswordEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) ? 'not-allowed' : 'pointer',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSendingReset && forgotPasswordEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
                                                e.currentTarget.style.opacity = '0.9';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSendingReset && forgotPasswordEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
                                                e.currentTarget.style.opacity = '1';
                                            }
                                        }}
                                    >
                                        {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                                <div style={themedStyles.alert.success}>
                                    <span style={{ fontSize: '20px', flexShrink: 0 }}>✅</span>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                                        <p style={{
                                            margin: 0,
                                            fontWeight: theme.typography.fontWeight.semibold,
                                            fontSize: theme.typography.fontSize.md,
                                            color: theme.colors.text.primary,
                                        }}>
                                            Reset Link Sent
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                            <p style={{
                                                margin: 0,
                                                fontSize: theme.typography.fontSize.sm,
                                                lineHeight: 1.6,
                                                color: theme.colors.text.secondary,
                                            }}>
                                                We've sent a password reset link to:
                                            </p>
                                            <p style={{
                                                margin: 0,
                                                fontSize: theme.typography.fontSize.sm,
                                                lineHeight: 1.6,
                                                color: theme.colors.text.primary,
                                            }}>
                                                <span style={{
                                                    fontWeight: theme.typography.fontWeight.semibold,
                                                    backgroundColor: theme.colors.background,
                                                    padding: '4px 8px',
                                                    borderRadius: theme.borderRadius.sm,
                                                    display: 'inline-block',
                                                }}>
                                                    {forgotPasswordEmail}
                                                </span>
                                            </p>
                                            <p style={{
                                                margin: 0,
                                                marginTop: theme.spacing.xs,
                                                fontSize: theme.typography.fontSize.sm,
                                                lineHeight: 1.6,
                                                color: theme.colors.text.secondary,
                                            }}>
                                                If an account exists with this email, you'll receive instructions to reset your password.
                                            </p>
                                            <p style={{
                                                margin: 0,
                                                marginTop: theme.spacing.sm,
                                                fontSize: theme.typography.fontSize.sm,
                                                lineHeight: 1.6,
                                                fontWeight: theme.typography.fontWeight.medium,
                                                color: theme.colors.text.primary,
                                            }}>
                                                Please check your spam folder if you don't see the email.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForgotPasswordModal(false);
                                        setResetEmailSent(false);
                                        setForgotPasswordEmail('');
                                    }}
                                    style={themedStyles.button.primary}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '0.9';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '1';
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </Modal>
                </div>
            </div>
        </>
    );
};

